import React from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';

import { usePullToRefresh } from 'Hooks/usePullToRefresh';

import './PullToRefresh.css';

/**
 * Wrapper component that adds pull-to-refresh functionality.
 * Shows a spinner indicator above the content that responds to pull gestures.
 * Uses usePullToRefresh hook for touch event logic.
 *
 * @param {{ children: React.ReactNode, onRefresh: () => Promise<void> }} props
 */
const PullToRefresh = ({ children, onRefresh }) => {
  const { pullDistance, isRefreshing, handlers } = usePullToRefresh(onRefresh);

  // Calculate opacity and scale based on pull distance (0-60px → 0-1)
  const progress = Math.min(pullDistance / 60, 1);

  return (
    <div
      className="pull-to-refresh"
      onTouchStart={handlers.onTouchStart}
      onTouchMove={handlers.onTouchMove}
      onTouchEnd={handlers.onTouchEnd}
    >
      {/* Refresh indicator */}
      {(pullDistance > 0 || isRefreshing) && (
        <motion.div
          className={`pull-to-refresh__indicator${isRefreshing ? ' pull-to-refresh__indicator--refreshing' : ''}`}
          style={{
            opacity: isRefreshing ? 1 : progress,
            transform: `translateX(-50%) scale(${isRefreshing ? 1 : progress})`,
          }}
          animate={isRefreshing ? { rotate: 360 } : {}}
          transition={isRefreshing ? { duration: 1, repeat: Infinity, ease: 'linear' } : {}}
        >
          <i className="bi bi-arrow-clockwise" />
        </motion.div>
      )}

      {children}
    </div>
  );
};

PullToRefresh.propTypes = {
  children: PropTypes.node.isRequired,
  onRefresh: PropTypes.func.isRequired,
};

export default PullToRefresh;
